import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import * as z from 'zod'

import { useHasRendered } from './use-has-rendered'

export const useStorage = <
  Schema extends z.ZodTypeAny,
  InitialValue extends NonNullable<z.infer<Schema>> | null,
>(
  type: 'localStorage' | 'sessionStorage',
  getSchema: (zod: typeof z) => Schema,
  key: string,
  options?: {
    initialValue: InitialValue
    enabled?: boolean
    dangerouslyCalculateOnHydration?: boolean
    debug?: boolean
  }
) => {
  type Value = z.infer<Schema>
  const schema = React.useMemo(() => getSchema(z), [getSchema])
  const isHydrated = useHasRendered()
  const isDisabled = options?.enabled === false || !isHydrated
  const initialValue = options?.initialValue ?? null
  const valueRef = React.useRef<Value | null>(initialValue)

  const query = useQuery<Value, unknown, unknown, [string]>({
    queryKey: [key],
    queryFn: ({ queryKey }) => {
      if (!isHydrated && !options?.dangerouslyCalculateOnHydration) {
        if (options?.debug) {
          // eslint-disable-next-line no-console
          console.log(`${key} initialValue not hydrated`, initialValue)
        }
        return initialValue
      }
      const storedValue = window[type].getItem(queryKey[0])
      let newValue = initialValue
      try {
        newValue =
          storedValue !== undefined && storedValue !== null
            ? schema.parse(JSON.parse(storedValue))
            : initialValue
      } catch (error) {
        newValue = initialValue
      }
      valueRef.current = newValue
      if (options?.debug) {
        // eslint-disable-next-line no-console
        console.log(`${key} newValue`, newValue)
      }
      return newValue
    },
    initialData: options?.initialValue,
    enabled: !isDisabled,
  })

  // eslint-disable-next-line react-compiler/react-compiler
  valueRef.current = query.data

  if (options?.debug) {
    // eslint-disable-next-line no-console
    console.log(`${key} query.data`, query.data)
  }

  const [hasSetInitialValue, setHasSetInitialValue] = React.useState(false)

  React.useLayoutEffect(() => {
    if (!isHydrated && !options?.dangerouslyCalculateOnHydration) return
    if (isDisabled) return
    if (hasSetInitialValue) return
    setHasSetInitialValue(true)
    const storedValue = window[type].getItem(key)
    let newValue = initialValue
    try {
      newValue =
        storedValue !== undefined && storedValue !== null
          ? schema.parse(JSON.parse(storedValue))
          : initialValue
    } catch (error) {
      newValue = initialValue
    }

    valueRef.current = newValue
  }, [
    isDisabled,
    initialValue,
    isHydrated,
    key,
    options?.dangerouslyCalculateOnHydration,
    query.data,
    schema,
    type,
    hasSetInitialValue,
  ])

  const queryClient = useQueryClient()

  const setValue = React.useCallback(
    async (valueOrUpdater: ValueOrUpdater<Value>) => {
      if (isDisabled) return
      const nextValue =
        typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prev: Value | undefined) => Value)(
              queryClient.getQueryData([key])
            )
          : valueOrUpdater
      if (nextValue) {
        window[type].setItem(
          key,
          nextValue ? JSON.stringify(nextValue) : 'undefined'
        )
      } else {
        window[type].removeItem(key)
      }
      await queryClient.refetchQueries([key])
      valueRef.current = nextValue
    },
    [isDisabled, key, queryClient, type]
  )

  React.useEffect(() => {
    if (isDisabled) return
    const listener = (event: StorageEvent) => {
      if (event.key === key) {
        queryClient.invalidateQueries([key])
      }
    }
    window.addEventListener('storage', listener)
    return () => window.removeEventListener('storage', listener)
  }, [isDisabled, key, queryClient])

  // eslint-disable-next-line react-compiler/react-compiler
  return [
    // eslint-disable-next-line react-compiler/react-compiler
    valueRef.current as InitialValue extends null ? Value | null : Value,
    setValue,
    query,
    query.data as InitialValue extends null ? Value | null : Value,
    valueRef,
  ] as const
}

type ValueOrUpdater<Value> = ((prev: Value | undefined) => Value) | Value
